import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import {
  Bleed,
  Button,
  Checkbox,
  colors,
  Expander,
  ExpanderContent,
  Grid,
  Row,
  Separator,
  Skeleton,
  useContainerWidth,
} from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { Link } from '@fs/zion-router'
import { sgBaseUrl } from '@fs/zion-config'
import { useQuery } from '@apollo/client/react'
import { useExperiment } from '@fs/zion-experiments'
import { trackLink } from '@fs/zion-analytics'

import TextHighlighter from 'components/TextHighlighter/TextHighlighter'
import { cleanUpAuthors, cleanUpNotes, encodedArk } from 'helpers'
import { previewPane } from 'queries'
import transl from './VolumeSetPreviewPane.transl'

const previewCss = css`
  > [class^='gridCss'] > [class^='cellCss'] + [class^='cellCss'] {
    padding-inline-start: 0;
  }
`
const leftColumnCss = css`
  background-color: ${colors.gray02};
  flex-grow: 1;
  margin-bottom: 20px;
  margin-inline-start: 40px;
  padding: 15px;

  [class^='gridCss'] {
    margin: 0;
  }
`
const columnCss = css`
  [class^='cellCss'] {
    width: 50%;
  }
`
const rightSideCss = css`
  border-inline-start: 1px solid ${colors.gray10};
  height: 100%;
  padding-inline-start: 20px;
`
const toggleButtonCss = css`
  button[class^='buttonCss'] {
    font-size: ${i18n.language.startsWith('zh') ? '14px' : '12px'};
    height: 32px;
    padding: 8px;
  }
`
const rightColumnCss = css`
  background-color: ${colors.gray02};
  flex-grow: 1;
  padding: 15px;

  [class^='cellCss'] + [class^='cellCss'] {
    padding-inline-start: 0;
  }
`
const noImageMessageCss = css`
  height: 100%;
  padding: 10px 20px 20px;
`
const hiddenPreviewImagesCss = css`
  height: 0;
  visibility: hidden;
`

export default function VolumeSetPreviewPane({
  groupId,
  myGroupIds,
  searchCriteria,
  setMyGroupIds,
  valuesToHighlight,
}) {
  const atWidth = useContainerWidth()
  const [imageDetailsUrlEx] = useExperiment('imageDetailsUrlEx')
  const { data } = useQuery(previewPane, {
    variables: {
      groupId,
      multipleClanBranches: true,
    },
  })
  const [ancestorsOpen, setAncestorsOpen] = useState(false)
  const [showPreviewImages, setShowPreviewImages] = useState(false)

  function handleImageLoaded() {
    setShowPreviewImages(true)
  }

  function toggleMyGroupId({ target: { checked } }) {
    trackLink({ name: `Checkbox:My volumeSet:Toggle-${groupId}` })
    const newGroupIds = checked ? [...myGroupIds, groupId] : myGroupIds.filter((id) => id !== groupId)
    setMyGroupIds(newGroupIds)
  }

  const { volumeSetSearch } = searchCriteria

  if (!data?.group) {
    return <VolumeSetPreviewSkeleton />
  }

  const href = imageDetailsUrlEx
    ? `${window.location.origin}/ark:/61903/${encodedArk(
        data.group
      )}?view=explore&groupId=${groupId}&volumeSetSearch=${volumeSetSearch}`
    : `${window.location.origin}/records/images/image-details?rmsId=${groupId}&${new URLSearchParams(
        searchCriteria
      ).toString()}`

  const notes = cleanUpNotes(data.group.titleNotes)
  const imageUrls = data.group.imageApids?.map(getApidThumbnailUrl)
  const childGroupCount = data.group.childGroupCount?.toLocaleString(i18n.language)
  const checkboxLabels = {
    jiapu: transl.myJiapuCheckbox(),
    jokbo: transl.myJokboCheckbox(),
    keizu: transl.myKeizuCheckbox(),
  }

  return (
    <div css={atWidth({ lg: previewCss })}>
      <Grid>
        <Bleed bottom columns={atWidth({ default: 4, sm: 5, md: 6, lg: 7 })} top>
          <div css={leftColumnCss}>
            <Grid>
              <Checkbox
                checked={myGroupIds.includes(groupId)}
                columns={12}
                label={checkboxLabels[volumeSetSearch]}
                name="myRecordCheckbox"
                onChange={toggleMyGroupId}
              />
              <div columns={atWidth({ default: 4, sm: 5, md: 6, lg: 8, xl: 4 })} css={columnCss}>
                <Row>
                  {transl.surname()}
                  {data.group.surnames?.join(', ')}
                </Row>
                {volumeSetSearch === 'jokbo' && (
                  <>
                    <Row>
                      {transl.clan()}
                      {data.group.clans?.join(', ')}
                    </Row>
                    <Row>
                      {transl.clanBranches()} {data.group.clanBranches?.join(', ')}
                    </Row>
                  </>
                )}
                <Row>
                  {transl.place()}
                  {[
                    ...new Set(data.group.coverages?.map((coverage) => coverage?.place?.fullName).filter(Boolean)),
                  ].join('; ')}
                </Row>
                <Row>
                  {transl.date()}
                  <TextHighlighter textToHighlight={valuesToHighlight}>{data.group.publicationDate}</TextHighlighter>
                </Row>
                <Row>
                  {transl.volumes()}
                  {imageUrls?.length ? (
                    <Button external to={href}>
                      {childGroupCount}
                    </Button>
                  ) : (
                    childGroupCount
                  )}
                </Row>
                <Row>
                  {transl.ancestors()}
                  <Expander open={ancestorsOpen} setOpen={setAncestorsOpen}>
                    {data.group.ancestors.slice(0, 3).map((ancestor) => (
                      <div key={ancestor}>
                        <TextHighlighter textToHighlight={valuesToHighlight}>{ancestor}</TextHighlighter>
                      </div>
                    ))}
                    {data.group.ancestors.length > 3 && (
                      <>
                        <ExpanderContent collapsedHeight={3}>
                          {data.group.ancestors.slice(3).map((ancestor) => (
                            <div key={ancestor}>
                              <TextHighlighter textToHighlight={valuesToHighlight}>{ancestor}</TextHighlighter>
                            </div>
                          ))}
                        </ExpanderContent>
                        <div css={toggleButtonCss}>
                          <Button onClick={() => setAncestorsOpen(!ancestorsOpen)}>
                            {ancestorsOpen ? transl.showLess() : transl.showMore()}
                          </Button>
                        </div>
                      </>
                    )}
                  </Expander>
                </Row>
                {volumeSetSearch === 'jiapu' && (
                  <Row>
                    {transl.ancestralHall()}
                    <div>
                      <TextHighlighter textToHighlight={valuesToHighlight}>{data.group.ancestralHall}</TextHighlighter>
                    </div>
                  </Row>
                )}
                <Row>
                  {transl.author()}
                  <TextHighlighter textToHighlight={valuesToHighlight}>
                    {cleanUpAuthors(data.group.authors)?.join(', ')}
                  </TextHighlighter>
                </Row>

                {atWidth({
                  default: (
                    <Row>
                      <GenerationPoemAndNotes
                        generationPoem={data.group.generationPoem}
                        notes={notes}
                        valuesToHighlight={valuesToHighlight}
                        volumeSetSearch={volumeSetSearch}
                      />
                    </Row>
                  ),
                  xl: null,
                })}
              </div>

              {atWidth({
                default: null,
                xl: (
                  <div columns={atWidth({ default: 4, sm: 5, md: 6, lg: 8, xl: 3 })} css={rightSideCss}>
                    <GenerationPoemAndNotes
                      generationPoem={data.group.generationPoem}
                      notes={notes}
                      valuesToHighlight={valuesToHighlight}
                      volumeSetSearch={volumeSetSearch}
                    />
                  </div>
                ),
              })}
            </Grid>
          </div>
        </Bleed>

        <Bleed bottom columns={atWidth({ default: 12, lg: 5, xl: 4 })} top>
          <Grid>
            <div
              css={atWidth({ default: leftColumnCss, lg: rightColumnCss })}
              columns={atWidth({ default: 4, sm: 5, md: 6, lg: 8, xl: 4 })}
            >
              {!imageUrls?.length ? (
                <Row alignX="center" css={noImageMessageCss}>
                  {transl.noImageWarningMessage()}
                </Row>
              ) : (
                <>
                  <Row alignX="center">
                    <Button emphasis="high" external to={href}>
                      {transl.viewImages()}
                    </Button>
                  </Row>

                  <div css={!showPreviewImages && hiddenPreviewImagesCss}>
                    <Separator size="xs" />

                    <Row alignX="center">
                      {imageUrls?.slice(0, 3).map((imageUrl) => (
                        <PreviewImage href={href} key={imageUrl} onLoad={handleImageLoaded} src={imageUrl} />
                      ))}
                    </Row>

                    {imageUrls?.length > 3 && (
                      <Row alignX="center">
                        {imageUrls?.slice(3, 6).map((imageUrl) => (
                          <PreviewImage href={href} key={imageUrl} onLoad={handleImageLoaded} src={imageUrl} />
                        ))}
                      </Row>
                    )}
                  </div>
                </>
              )}
            </div>
          </Grid>
        </Bleed>
      </Grid>
    </div>
  )
}

VolumeSetPreviewPane.propTypes = {
  groupId: PropTypes.string.isRequired,
  myGroupIds: PropTypes.arrayOf(PropTypes.string),
  searchCriteria: PropTypes.shape({}).isRequired,
  setMyGroupIds: PropTypes.func,
  valuesToHighlight: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
}

const labelCss = css`
  margin-bottom: 10px;
`
const indentCss = css`
  padding-inline-start: 20px;
`

function GenerationPoemAndNotes({ generationPoem, notes, valuesToHighlight, volumeSetSearch }) {
  const [notesOpen, setNotesOpen] = useState(false)

  return (
    <>
      <div css={labelCss}>{transl.notes()}</div>
      {notes && (
        <div css={indentCss} data-testid="jiapu-notes">
          <Expander open={notesOpen} setOpen={setNotesOpen}>
            {notes.slice(0, 2).map((note) => (
              <div key={note}>
                <TextHighlighter textToHighlight={valuesToHighlight}>{note}</TextHighlighter>
              </div>
            ))}
            {notes.length > 2 && (
              <>
                <ExpanderContent collapsedHeight={3}>
                  {notes.slice(2, notes.length).map((note) => (
                    <div key={note}>
                      <TextHighlighter textToHighlight={valuesToHighlight}>{note}</TextHighlighter>
                    </div>
                  ))}
                </ExpanderContent>
                <div css={toggleButtonCss}>
                  <Button onClick={() => setNotesOpen(!notesOpen)}>
                    {notesOpen ? transl.showLess() : transl.showMore()}
                  </Button>
                </div>
              </>
            )}
          </Expander>
        </div>
      )}
      {volumeSetSearch === 'jiapu' && (
        <>
          <div css={labelCss}>{transl.generationPoem()}</div>
          <div css={indentCss}>
            <TextHighlighter textToHighlight={valuesToHighlight}>{generationPoem}</TextHighlighter>
          </div>
        </>
      )}
    </>
  )
}

GenerationPoemAndNotes.propTypes = {
  generationPoem: PropTypes.string,
  notes: PropTypes.arrayOf(PropTypes.string.isRequired),
  valuesToHighlight: PropTypes.arrayOf(PropTypes.string.isRequired),
  volumeSetSearch: PropTypes.string.isRequired,
}

const previewImageCss = css`
  width: 100%;
  max-width: 125px;
  max-height: 170px;
`

function PreviewImage({ href, onLoad, src }) {
  return (
    <Link to={href}>
      <img alt={transl.recordPreview()} css={previewImageCss} key={src} loading="lazy" onLoad={onLoad} src={src} />
    </Link>
  )
}

PreviewImage.propTypes = {
  href: PropTypes.string.isRequired,
  onLoad: PropTypes.func.isRequired,
  src: PropTypes.string.isRequired,
}

function getApidThumbnailUrl(apid) {
  return `${sgBaseUrl}/service/records/storage/deepzoomcloud/dz/v1/apid:${apid}/thumb_p200.jpg`
}

/**
 * Loading skeleton that mirrors the VolumeSetPreviewPane layout structure
 * Uses ZionUI Skeleton components to provide visual placeholders while data loads
 */
function VolumeSetPreviewSkeleton() {
  const atWidth = useContainerWidth()

  return (
    <div css={atWidth({ lg: previewCss })}>
      <Grid>
        {/* Left Column - Volume Details */}
        <Bleed bottom columns={atWidth({ default: 4, sm: 5, md: 6, lg: 7 })} top>
          <div css={leftColumnCss}>
            <Grid>
              <Skeleton.Checkbox columns={12} />

              <div columns={atWidth({ default: 4, sm: 5, md: 6, lg: 8, xl: 4 })} css={columnCss}>
                <Row>
                  <Skeleton.Paragraph maxWidth={100} size="sm" />
                  <Skeleton.Paragraph maxWidth={200} size="sm" />
                </Row>

                <Row>
                  <Skeleton.Paragraph maxWidth={100} size="sm" />
                  <Skeleton.Paragraph maxWidth={200} size="sm" />
                </Row>

                <Row>
                  <Skeleton.Paragraph maxWidth={100} size="sm" />
                  <Skeleton.Paragraph maxWidth={300} size="sm" />
                </Row>

                <Row>
                  <Skeleton.Paragraph maxWidth={100} size="sm" />
                  <Skeleton.Paragraph maxWidth={150} size="sm" />
                </Row>

                <Row>
                  <Skeleton.Paragraph maxWidth={100} size="sm" />
                  <Skeleton.Button minWidth={80} />
                </Row>

                <Row>
                  <Skeleton.Paragraph maxWidth={100} size="sm" />
                  <div>
                    <Skeleton.Paragraph maxWidth={200} size="sm" />
                    <Skeleton.Paragraph maxWidth={200} size="sm" />
                    <Skeleton.Paragraph maxWidth={200} size="sm" />
                    <Skeleton.Button />
                  </div>
                </Row>

                <Row>
                  <Skeleton.Paragraph maxWidth={100} size="sm" />
                  <Skeleton.Paragraph maxWidth={200} size="sm" />
                </Row>

                <Row>
                  <Skeleton.Paragraph maxWidth={100} size="sm" />
                  <Skeleton.Paragraph maxWidth={250} size="sm" />
                </Row>

                {atWidth({
                  default: (
                    <Row>
                      <div>
                        <Skeleton.Paragraph maxWidth={300} size="sm" />
                        <Skeleton.Paragraph maxWidth={250} size="sm" />
                      </div>
                    </Row>
                  ),
                  xl: null,
                })}
              </div>

              {atWidth({
                default: null,
                xl: (
                  <div columns={atWidth({ default: 4, sm: 5, md: 6, lg: 8, xl: 3 })} css={rightSideCss}>
                    <div>
                      <Skeleton.Paragraph maxWidth={300} size="sm" />
                      <Skeleton.Paragraph maxWidth={250} size="sm" />
                    </div>
                  </div>
                ),
              })}
            </Grid>
          </div>
        </Bleed>

        {/* Right Column - Images */}
        <Bleed bottom columns={atWidth({ default: 12, lg: 5, xl: 4 })} top>
          <Grid>
            <div
              css={atWidth({ default: leftColumnCss, lg: rightColumnCss })}
              columns={atWidth({ default: 4, sm: 5, md: 6, lg: 8, xl: 4 })}
            >
              <Row alignX="center">
                <Skeleton.Button emphasis="high" minWidth={150} />
              </Row>

              <div>
                <Separator size="xs" />

                <Row alignX="center">
                  <Skeleton.Image height={120} width={120} />
                  <Skeleton.Image height={120} width={120} />
                  <Skeleton.Image height={120} width={120} />
                </Row>

                <Row alignX="center">
                  <Skeleton.Image height={120} width={120} />
                  <Skeleton.Image height={120} width={120} />
                  <Skeleton.Image height={120} width={120} />
                </Row>
              </div>
            </div>
          </Grid>
        </Bleed>
      </Grid>
    </div>
  )
}
